import React, { useState, useEffect } from "react";

type Props = {};

// Define difficulty levels
type Difficulty = "easy" | "medium" | "hard";

// Board cell value
type CellValue = "X" | "O" | null;

// Player type
type Player = "X" | "O";

export default function TikTakToeGamePage({}: Props) {
  // Game settings
  const [boardSize, setBoardSize] = useState(3);
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [playerSymbol, setPlayerSymbol] = useState<Player>("X");
  const [aiSymbol, setAiSymbol] = useState<Player>("O");
  const [currentPlayer, setCurrentPlayer] = useState<Player>("X");
  const [gameStarted, setGameStarted] = useState(false);
  const [showSettings, setShowSettings] = useState(true);

  // Game state
  const [board, setBoard] = useState<CellValue[][]>([]);
  const [winner, setWinner] = useState<Player | "draw" | null>(null);
  const [winningCells, setWinningCells] = useState<[number, number][]>([]);
  const [scores, setScores] = useState({ player: 0, ai: 0, draws: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [cellAnimations, setCellAnimations] = useState<{
    [key: string]: string;
  }>({});
  useEffect(() => {
    if (currentPlayer === aiSymbol && !gameOver) {
      const timer = setTimeout(() => makeAiMove(), 500);
      return () => clearTimeout(timer);
    }
  }, [currentPlayer, gameOver, aiSymbol, board]);
  // Themes and appearance
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const themes = {
    light: {
      background: "#ffffff",
      primaryBg: "#f5f7fa",
      secondaryBg: "#edf2f7",
      primaryText: "#2d3748",
      secondaryText: "#4a5568",
      accent: "#3182ce",
      accentHover: "#2b6cb0",
      playerX: "#e53e3e",
      playerO: "#3182ce",
      border: "#e2e8f0",
      shadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    dark: {
      background: "#1a202c",
      primaryBg: "#2d3748",
      secondaryBg: "#4a5568",
      primaryText: "#f7fafc",
      secondaryText: "#e2e8f0",
      accent: "#4299e1",
      accentHover: "#3182ce",
      playerX: "#fc8181",
      playerO: "#63b3ed",
      border: "#4a5568",
      shadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
    },
  };

  const currentTheme = themes[theme];

  // Initialize board when size changes
  useEffect(() => {
    initializeBoard();
  }, [boardSize]);

  // Reset game when settings change
  useEffect(() => {
    if (gameStarted) {
      initializeBoard();
      setGameOver(false);
      setWinner(null);
      setWinningCells([]);
      setMessage("");

      // If AI goes first, make a move
      if (currentPlayer === aiSymbol) {
        setTimeout(() => makeAiMove(), 500);
      }
    }
  }, [gameStarted, playerSymbol, aiSymbol, difficulty, boardSize]);

  const initializeBoard = () => {
    const newBoard: CellValue[][] = [];
    for (let i = 0; i < boardSize; i++) {
      newBoard.push(Array(boardSize).fill(null));
    }
    setBoard(newBoard);
  };

  const handleCellClick = (row: number, col: number) => {
    // Ignore if game over, cell is filled, or not player's turn
    if (
      gameOver ||
      board[row][col] !== null ||
      currentPlayer !== playerSymbol
    ) {
      return;
    }

    // Update board state using functional update
    setBoard((prevBoard) =>
      prevBoard.map((r, i) =>
        i === row ? r.map((c, j) => (j === col ? playerSymbol : c)) : r
      )
    );

    // Check for win or draw using the updated board
    const newBoard = board.map((r, i) =>
      i === row ? r.map((c, j) => (j === col ? playerSymbol : c)) : r
    );
    const result = checkGameResult(newBoard, row, col, playerSymbol);
    if (result) {
      handleGameEnd(result, playerSymbol);
    } else {
      // Switch to AI's turn
      setCurrentPlayer(aiSymbol);
    }
  };

  const makeAiMove = () => {
    if (gameOver) return;

    let row: number, col: number;

    switch (difficulty) {
      case "easy":
        [row, col] = makeRandomMove();
        break;
      case "medium":
        // 50% chance for optimal move, 50% chance for random
        if (Math.random() < 0.5) {
          [row, col] = findBestMove();
        } else {
          [row, col] = makeRandomMove();
        }
        break;
      case "hard":
        [row, col] = findBestMove();
        break;
      default:
        [row, col] = makeRandomMove();
    }

    if (row !== undefined && col !== undefined) {
      // Update board state using functional update
      setBoard((prevBoard) =>
        prevBoard.map((r, i) =>
          i === row ? r.map((c, j) => (j === col ? aiSymbol : c)) : r
        )
      );

      // Check for win or draw using the updated board
      const newBoard = board.map((r, i) =>
        i === row ? r.map((c, j) => (j === col ? aiSymbol : c)) : r
      );
      const result = checkGameResult(newBoard, row, col, aiSymbol);
      if (result) {
        handleGameEnd(result, aiSymbol);
      } else {
        // Switch back to player's turn
        setCurrentPlayer(playerSymbol);
      }
    }
  };

  const makeRandomMove = (): [number, number] => {
    const emptyCells: [number, number][] = [];

    for (let i = 0; i < boardSize; i++) {
      for (let j = 0; j < boardSize; j++) {
        if (board[i][j] === null) {
          emptyCells.push([i, j]);
        }
      }
    }

    if (emptyCells.length === 0) return [-1, -1];

    const randomIndex = Math.floor(Math.random() * emptyCells.length);
    return emptyCells[randomIndex];
  };

  // Minimax algorithm for unbeatable AI
  const findBestMove = (): [number, number] => {
    let bestScore = -Infinity;
    let bestMove: [number, number] = [-1, -1];

    // Limit depth for larger boards to prevent long calculations
    const maxDepth = boardSize > 3 ? 3 : 5;

    for (let i = 0; i < boardSize; i++) {
      for (let j = 0; j < boardSize; j++) {
        if (board[i][j] === null) {
          // Try this move
          const newBoard = JSON.parse(JSON.stringify(board)) as CellValue[][];
          newBoard[i][j] = aiSymbol;

          // Calculate score for this move
          const score = minimax(
            newBoard,
            0,
            false,
            -Infinity,
            Infinity,
            maxDepth
          );

          if (score > bestScore) {
            bestScore = score;
            bestMove = [i, j];
          }
        }
      }
    }

    return bestMove;
  };

  const minimax = (
    board: CellValue[][],
    depth: number,
    isMaximizing: boolean,
    alpha: number,
    beta: number,
    maxDepth: number
  ): number => {
    // Check for terminal states
    const result = evaluateBoard(board);
    if (result.gameOver || depth === maxDepth) {
      if (result.winner === aiSymbol) return 10 - depth;
      if (result.winner === playerSymbol) return depth - 10;
      return 0; // Draw
    }

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (let i = 0; i < boardSize; i++) {
        for (let j = 0; j < boardSize; j++) {
          if (board[i][j] === null) {
            const newBoard = JSON.parse(JSON.stringify(board)) as CellValue[][];
            newBoard[i][j] = aiSymbol;
            const score = minimax(
              newBoard,
              depth + 1,
              false,
              alpha,
              beta,
              maxDepth
            );
            bestScore = Math.max(score, bestScore);
            alpha = Math.max(alpha, bestScore);
            if (beta <= alpha) break;
          }
        }
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (let i = 0; i < boardSize; i++) {
        for (let j = 0; j < boardSize; j++) {
          if (board[i][j] === null) {
            const newBoard = JSON.parse(JSON.stringify(board)) as CellValue[][];
            newBoard[i][j] = playerSymbol;
            const score = minimax(
              newBoard,
              depth + 1,
              true,
              alpha,
              beta,
              maxDepth
            );
            bestScore = Math.min(score, bestScore);
            beta = Math.min(beta, bestScore);
            if (beta <= alpha) break;
          }
        }
      }
      return bestScore;
    }
  };

  const evaluateBoard = (board: CellValue[][]) => {
    // Check rows, columns, and diagonals
    // Rows
    for (let i = 0; i < boardSize; i++) {
      if (board[i][0] && board[i].every((cell) => cell === board[i][0])) {
        return {
          gameOver: true,
          winner: board[i][0] as Player,
          winningCells: Array.from(
            { length: boardSize },
            (_, j) => [i, j] as [number, number]
          ),
        };
      }
    }

    // Columns
    for (let j = 0; j < boardSize; j++) {
      if (
        board[0][j] &&
        Array.from({ length: boardSize }, (_, i) => board[i][j]).every(
          (cell) => cell === board[0][j]
        )
      ) {
        return {
          gameOver: true,
          winner: board[0][j] as Player,
          winningCells: Array.from(
            { length: boardSize },
            (_, i) => [i, j] as [number, number]
          ),
        };
      }
    }

    // Main diagonal
    if (
      board[0][0] &&
      Array.from({ length: boardSize }, (_, i) => board[i][i]).every(
        (cell) => cell === board[0][0]
      )
    ) {
      return {
        gameOver: true,
        winner: board[0][0] as Player,
        winningCells: Array.from(
          { length: boardSize },
          (_, i) => [i, i] as [number, number]
        ),
      };
    }

    // Anti-diagonal
    if (
      board[0][boardSize - 1] &&
      Array.from(
        { length: boardSize },
        (_, i) => board[i][boardSize - 1 - i]
      ).every((cell) => cell === board[0][boardSize - 1])
    ) {
      return {
        gameOver: true,
        winner: board[0][boardSize - 1] as Player,
        winningCells: Array.from(
          { length: boardSize },
          (_, i) => [i, boardSize - 1 - i] as [number, number]
        ),
      };
    }

    // Check for draw
    const isDraw = board.flat().every((cell) => cell !== null);
    if (isDraw) {
      return { gameOver: true, winner: "draw", winningCells: [] };
    }

    // Game still in progress
    return { gameOver: false, winner: null, winningCells: [] };
  };

  const checkGameResult = (
    board: CellValue[][],
    lastRow: number,
    lastCol: number,
    player: Player
  ): "win" | "draw" | null => {
    const result = evaluateBoard(board);

    if (result.gameOver) {
      if (result.winner === "draw") {
        setWinningCells([]);
        return "draw";
      } else if (result.winner) {
        setWinningCells(result.winningCells);
        return "win";
      }
    }

    return null;
  };

  const handleGameEnd = (result: "win" | "draw", player: Player) => {
    setGameOver(true);

    if (result === "win") {
      setWinner(player);
      setMessage(`${player === playerSymbol ? "You" : "AI"} won!`);

      if (player === playerSymbol) {
        setScores({ ...scores, player: scores.player + 1 });
      } else {
        setScores({ ...scores, ai: scores.ai + 1 });
      }
    } else {
      setWinner("draw");
      setMessage("It's a draw!");
      setScores({ ...scores, draws: scores.draws + 1 });
    }

    // Display animation after a short delay
    setTimeout(() => displayResultAnimation(result, player), 300);
  };

  // Add this function for closing the result modal
  const closeResultModal = () => {
    setShowResultModal(false);
  };

  // Add this to the handleNewGame function to ensure modal is closed
  const handleNewGame = () => {
    initializeBoard();
    setGameOver(false);
    setWinner(null);
    setWinningCells([]);
    setMessage("");
    setCurrentPlayer("X");
    setShowResultModal(false); // Close the modal
    setCellAnimations({}); // Clear animations

    // If AI goes first, make a move
    if (aiSymbol === "X") {
      setTimeout(() => makeAiMove(), 500);
    }
  };

  const displayResultAnimation = (result: "win" | "draw", player: Player) => {
    setShowResultModal(true);

    // Show confetti for player wins
    if (result === "win" && player === playerSymbol) {
      setConfetti(true);
      setTimeout(() => setConfetti(false), 4000);
    }

    // Animate cells based on the game result
    if (result === "win") {
      // Animate winning cells
      const animations: { [key: string]: string } = {};
      winningCells.forEach(([row, col]) => {
        animations[`${row}-${col}`] =
          player === playerSymbol ? "winPulse" : "losePulse";
      });
      setCellAnimations(animations);
    } else if (result === "draw") {
      // Animate all cells for draw
      const animations: { [key: string]: string } = {};
      for (let i = 0; i < boardSize; i++) {
        for (let j = 0; j < boardSize; j++) {
          animations[`${i}-${j}`] = "drawPulse";
        }
      }
      setCellAnimations(animations);
    }

    // Clear animations after a delay
    setTimeout(() => {
      setCellAnimations({});
    }, 3000);
  };

  const toggleSymbol = () => {
    setPlayerSymbol(playerSymbol === "X" ? "O" : "X");
    setAiSymbol(aiSymbol === "X" ? "O" : "X");
  };
  const renderResultModal = () => {
    if (!showResultModal) return null;

    const modalBgColor =
      winner === playerSymbol
        ? "rgba(72, 187, 120, 0.95)"
        : winner === aiSymbol
        ? "rgba(229, 62, 62, 0.95)"
        : "rgba(66, 153, 225, 0.95)";

    const modalIcon =
      winner === playerSymbol ? "🏆" : winner === aiSymbol ? "😢" : "🤝";

    const modalTitle =
      winner === playerSymbol
        ? "Congratulations!"
        : winner === aiSymbol
        ? "Better luck next time!"
        : "It's a draw!";

    const modalDescription =
      winner === playerSymbol
        ? "You won the game!"
        : winner === aiSymbol
        ? "The AI won this round."
        : "No winner this time.";

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          animation: "fadeIn 0.3s ease-out",
          padding: "15px", // Added padding for mobile
        }}
      >
        <div
          style={{
            backgroundColor: modalBgColor,
            borderRadius: "16px",
            padding: "20px", // Reduced from 30px for better mobile fit
            maxWidth: "400px",
            width: "100%", // Changed from 90% to 100%
            margin: "0 10px", // Added horizontal margin
            textAlign: "center",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            animation: "slideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            position: "relative",
            boxSizing: "border-box", // Added to prevent overflow
          }}
        >
          <div
            style={{
              fontSize: "60px",
              marginBottom: "10px",
              animation: "bounce 1s infinite alternate",
            }}
          >
            {modalIcon}
          </div>
          <h2
            style={{
              color: "#fff",
              fontSize: "28px",
              marginBottom: "15px",
            }}
          >
            {modalTitle}
          </h2>
          <p
            style={{
              color: "#fff",
              fontSize: "18px",
              marginBottom: "25px",
              opacity: 0.9,
            }}
          >
            {modalDescription}
          </p>
          <button
            style={{
              padding: "12px 30px",
              borderRadius: "50px",
              backgroundColor: "#fff",
              color:
                winner === playerSymbol
                  ? "#38a169"
                  : winner === aiSymbol
                  ? "#e53e3e"
                  : "#3182ce",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
              fontWeight: "bold",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 10px rgba(0, 0, 0, 0.1)";
            }}
            onClick={handleNewGame}
          >
            Play Again
          </button>

          <button
            style={{
              position: "absolute",
              top: "15px",
              right: "15px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.3)";
            }}
            onClick={closeResultModal}
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  // Add this function to render confetti effect
  const renderConfetti = () => {
    if (!confetti) return null;

    const confettiColors = [
      "#f94144",
      "#f3722c",
      "#f8961e",
      "#f9c74f",
      "#90be6d",
      "#43aa8b",
      "#577590",
      "#277da1",
    ];

    // Create 100 confetti particles
    return Array.from({ length: 100 }).map((_, i) => {
      const left = Math.random() * 100;
      const animationDuration = 3 + Math.random() * 2;
      const animationDelay = Math.random() * 0.5;
      const size = 8 + Math.random() * 10;
      const bgColor =
        confettiColors[Math.floor(Math.random() * confettiColors.length)];

      return (
        <div
          key={i}
          style={{
            position: "fixed",
            left: `${left}%`,
            top: "-20px",
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: bgColor,
            borderRadius: Math.random() > 0.5 ? "50%" : "0",
            opacity: 0.8,
            zIndex: 999,
            animation: `confettiFall ${animationDuration}s ease-in forwards ${animationDelay}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      );
    });
  };

  const startGame = () => {
    setGameStarted(true);
    setShowSettings(false);
    setCurrentPlayer("X");
    initializeBoard();

    // If AI goes first, make a move
    if (aiSymbol === "X") {
      setTimeout(() => makeAiMove(), 500);
    }
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const renderSettingsScreen = () => {
    return (
      <div
        style={{
          padding: "20px",
          borderRadius: "12px",
          backgroundColor: currentTheme.primaryBg,
          boxShadow: currentTheme.shadow,
          maxWidth: "500px",
          margin: "0 auto",
        }}
      >
        <h2
          style={{
            color: currentTheme.primaryText,
            textAlign: "center",
            marginBottom: "24px",
            fontSize: "28px",
            fontWeight: "bold",
          }}
        >
          Game Settings
        </h2>

        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              color: currentTheme.primaryText,
              marginBottom: "8px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Board Size:
          </label>
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            {[3, 4, 5].map((size) => (
              <button
                key={size}
                style={{
                  padding: "10px 15px",
                  borderRadius: "8px",
                  backgroundColor:
                    boardSize === size
                      ? currentTheme.accent
                      : currentTheme.secondaryBg,
                  color: boardSize === size ? "#fff" : currentTheme.primaryText,
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  flex: "1",
                  transition: "all 0.2s ease",
                }}
                onClick={() => setBoardSize(size)}
              >
                {size}x{size}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              display: "block",
              color: currentTheme.primaryText,
              marginBottom: "8px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Difficulty:
          </label>
          <div
            style={{
              display: "flex",
              gap: "10px",
            }}
          >
            {["easy", "medium", "hard"].map((level) => (
              <button
                key={level}
                style={{
                  padding: "10px 15px",
                  borderRadius: "8px",
                  backgroundColor:
                    difficulty === level
                      ? currentTheme.accent
                      : currentTheme.secondaryBg,
                  color:
                    difficulty === level ? "#fff" : currentTheme.primaryText,
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  flex: "1",
                  textTransform: "capitalize",
                  transition: "all 0.2s ease",
                }}
                onClick={() => setDifficulty(level as Difficulty)}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            marginBottom: "30px",
          }}
        >
          <label
            style={{
              display: "block",
              color: currentTheme.primaryText,
              marginBottom: "8px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Your Symbol:
          </label>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            <button
              style={{
                padding: "15px 25px",
                borderRadius: "8px",
                backgroundColor:
                  playerSymbol === "X"
                    ? currentTheme.playerX
                    : currentTheme.secondaryBg,
                color: playerSymbol === "X" ? "#fff" : currentTheme.primaryText,
                border: "none",
                cursor: "pointer",
                fontSize: "24px",
                fontWeight: "bold",
                width: "70px",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setPlayerSymbol("X");
                setAiSymbol("O");
              }}
            >
              X
            </button>
            <button
              style={{
                padding: "15px 25px",
                borderRadius: "8px",
                backgroundColor:
                  playerSymbol === "O"
                    ? currentTheme.playerO
                    : currentTheme.secondaryBg,
                color: playerSymbol === "O" ? "#fff" : currentTheme.primaryText,
                border: "none",
                cursor: "pointer",
                fontSize: "24px",
                fontWeight: "bold",
                width: "70px",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                setPlayerSymbol("O");
                setAiSymbol("X");
              }}
            >
              O
            </button>
          </div>
        </div>

        <button
          style={{
            padding: "15px 20px",
            borderRadius: "8px",
            backgroundColor: currentTheme.accent,
            color: "#fff",
            border: "none",
            cursor: "pointer",
            fontSize: "18px",
            fontWeight: "bold",
            width: "100%",
            marginTop: "10px",
            transition: "background-color 0.2s ease",
          }}
          onClick={startGame}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = currentTheme.accentHover)
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = currentTheme.accent)
          }
        >
          Start Game
        </button>
      </div>
    );
  };

  const renderGameBoard = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        {/* Game info */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: "20px",
            padding: "10px 20px",
            borderRadius: "10px",
            backgroundColor: currentTheme.primaryBg,
            boxShadow: currentTheme.shadow,
          }}
        >
          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{
                color: currentTheme.playerX,
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              X: {playerSymbol === "X" ? "You" : "AI"}
            </div>
            <div
              style={{
                color: currentTheme.primaryText,
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {playerSymbol === "X" ? scores.player : scores.ai}
            </div>
          </div>

          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{
                color: currentTheme.secondaryText,
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Draws
            </div>
            <div
              style={{
                color: currentTheme.primaryText,
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {scores.draws}
            </div>
          </div>

          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{
                color: currentTheme.playerO,
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              O: {playerSymbol === "O" ? "You" : "AI"}
            </div>
            <div
              style={{
                color: currentTheme.primaryText,
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {playerSymbol === "O" ? scores.player : scores.ai}
            </div>
          </div>
        </div>

        {/* Game message */}
        {message && (
          <div
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              backgroundColor:
                winner === playerSymbol
                  ? "rgba(72, 187, 120, 0.2)"
                  : winner === aiSymbol
                  ? "rgba(229, 62, 62, 0.2)"
                  : "rgba(66, 153, 225, 0.2)",
              color:
                winner === playerSymbol
                  ? "#38a169"
                  : winner === aiSymbol
                  ? "#e53e3e"
                  : "#3182ce",
              fontWeight: "bold",
              marginBottom: "20px",
              textAlign: "center",
              fontSize: "18px",
              width: "100%",
            }}
          >
            {message}
          </div>
        )}

        {/* Current turn indicator */}
        {!gameOver && (
          <div
            style={{
              marginBottom: "20px",
              color: currentTheme.secondaryText,
              fontWeight: "bold",
              fontSize: "18px",
            }}
          >
            Current turn:{" "}
            <span
              style={{
                color:
                  currentPlayer === "X"
                    ? currentTheme.playerX
                    : currentTheme.playerO,
                fontWeight: "bold",
              }}
            >
              {currentPlayer === playerSymbol ? "Your" : "AI's"} (
              {currentPlayer})
            </span>
          </div>
        )}

        {/* Game board */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${boardSize}, 1fr)`,
            gap: "10px",
            marginBottom: "20px",
            width: "100%",
          }}
        >
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const isWinningCell = winningCells.some(
                ([r, c]) => r === rowIndex && c === colIndex
              );

              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    aspectRatio: "1/1",
                    backgroundColor: isWinningCell
                      ? winner === "X"
                        ? "rgba(252, 129, 129, 0.3)"
                        : "rgba(99, 179, 237, 0.3)"
                      : currentTheme.primaryBg,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: `${Math.min(70, 140 / boardSize)}px`,
                    fontWeight: "bold",
                    cursor:
                      cell === null &&
                      !gameOver &&
                      currentPlayer === playerSymbol
                        ? "pointer"
                        : "default",
                    borderRadius: "8px",
                    boxShadow: currentTheme.shadow,
                    transition: "all 0.2s ease",
                    animation: cellAnimations[`${rowIndex}-${colIndex}`]
                      ? `${
                          cellAnimations[`${rowIndex}-${colIndex}`]
                        } 1s ease infinite`
                      : cell !== null
                      ? "fadeIn 0.3s"
                      : "",
                    color:
                      cell === "X"
                        ? currentTheme.playerX
                        : currentTheme.playerO,
                  }}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                  onMouseOver={(e) => {
                    if (
                      cell === null &&
                      !gameOver &&
                      currentPlayer === playerSymbol
                    ) {
                      e.currentTarget.style.transform = "scale(1.05)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 12px rgba(0, 0, 0, 0.15)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (
                      cell === null &&
                      !gameOver &&
                      currentPlayer === playerSymbol
                    ) {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow = currentTheme.shadow;
                    }
                  }}
                >
                  {cell}
                </div>
              );
            })
          )}
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "15px",
            marginTop: "10px",
            width: "100%",
          }}
        >
          <button
            style={{
              padding: "12px 20px",
              borderRadius: "8px",
              backgroundColor: currentTheme.secondaryBg,
              color: currentTheme.primaryText,
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
              flex: "1",
              transition: "background-color 0.2s ease",
            }}
            onClick={openSettings}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = currentTheme.border)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = currentTheme.secondaryBg)
            }
          >
            Settings
          </button>
          <button
            style={{
              padding: "12px 20px",
              borderRadius: "8px",
              backgroundColor: currentTheme.accent,
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
              flex: "1",
              transition: "background-color 0.2s ease",
            }}
            onClick={handleNewGame}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = currentTheme.accentHover)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = currentTheme.accent)
            }
          >
            {gameOver ? "Play Again" : "Restart"}
          </button>
        </div>
        {renderResultModal()}
        {renderConfetti()}
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: currentTheme.background,
        minHeight: "100vh",
        fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        padding: "20px",
        transition: "background-color 0.3s ease",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
          }}
        >
          <h1
            style={{
              color: currentTheme.primaryText,
              fontSize: "32px",
              fontWeight: "bold",
              margin: 0,
            }}
          >
            Tic-Tac-Toe
          </h1>
          <button
            style={{
              backgroundColor: "transparent",
              border: "none",
              color: currentTheme.primaryText,
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              transition: "background-color 0.2s ease",
            }}
            onClick={toggleTheme}
            title={
              theme === "light" ? "Switch to dark mode" : "Switch to light mode"
            }
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
        </div>

        {showSettings ? renderSettingsScreen() : renderGameBoard()}

        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            color: currentTheme.secondaryText,
            fontSize: "14px",
          }}
        >
          Created with ❤️ for your game platform
        </div>
      </div>

      {/* Keyframes for animations */}
      {/* Modify the style tag in the return statement to include these additional animations */}
      <style>
        {`
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(50px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes bounce {
      from { transform: scale(1); }
      to { transform: scale(1.1); }
    }
    
    @keyframes confettiFall {
      0% { transform: translateY(0) rotate(0); }
      100% { transform: translateY(100vh) rotate(720deg); }
    }
    
    @keyframes winPulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); box-shadow: 0 0 20px rgba(72, 187, 120, 0.6); }
      100% { transform: scale(1); }
    }
    
    @keyframes losePulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); box-shadow: 0 0 20px rgba(229, 62, 62, 0.6); }
      100% { transform: scale(1); }
    }
    
    @keyframes drawPulse {
      0% { transform: scale(1) rotate(0deg); }
      50% { transform: scale(1.05) rotate(5deg); }
      100% { transform: scale(1) rotate(0deg); }
    }
  `}
      </style>
    </div>
  );
}
